import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress, Typography } from '@mui/material';
import { QueryClient, QueryClientProvider } from 'react-query';
import { SnackbarProvider } from 'notistack';

// Components
import Layout from './components/Layout/Layout';
import Homepage from './pages/Homepage';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Cities from './pages/Cities';
import Lines from './pages/Lines';
import Stations from './pages/Stations';
import Places from './pages/Places';
import StationConnections from './pages/StationConnections';
import LineStations from './pages/LineStations';
import StationFacilities from './pages/StationFacilities';
import StationAmenities from './pages/StationAmenities';
import RoutePlanning from './pages/RoutePlanning';
import Analytics from './pages/Analytics';
import ApiLogs from './pages/ApiLogs';
import Plans from './pages/Plans';
import UserPlans from './pages/UserPlans';
import Users from './pages/Users';
import AppVersions from './pages/AppVersions';
import Journeys from './pages/Journeys';
import JourneyTesting from './pages/JourneyTesting';
import PushNotifications from './pages/PushNotifications';
import Incidents from './pages/Incidents';
import CacheManagement from './pages/CacheManagement';
import FareRules from './pages/FareRules';
import FareConditions from './pages/FareConditions';
import FeatureFlags from './pages/FeatureFlags';

// Context
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Create theme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#3498db',
      dark: '#2980b9',
      light: '#5dade2',
    },
    secondary: {
      main: '#2c3e50',
      dark: '#1a252f',
      light: '#34495e',
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
    text: {
      primary: '#2c3e50',
      secondary: '#6c757d',
    },
    divider: 'rgba(0,0,0,0.1)',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 800,
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 800,
      lineHeight: 1.2,
    },
    h3: {
      fontWeight: 700,
      lineHeight: 1.3,
    },
    h4: {
      fontWeight: 700,
      lineHeight: 1.3,
    },
    h5: {
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      lineHeight: 1.4,
    },
    body1: {
      lineHeight: 1.6,
    },
    body2: {
      lineHeight: 1.6,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          border: '1px solid rgba(0,0,0,0.05)',
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
  shape: {
    borderRadius: 12,
  },
});

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  // Show loading state while checking authentication
  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          backgroundColor: '#f5f5f5'
        }}
      >
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading...</Typography>
      </Box>
    );
  }
  
  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return <>{children}</>;
};

// Main App Component
const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarProvider
          maxSnack={3}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <AuthProvider>
            <Router>
              <Routes>
                {/* Public Homepage */}
                <Route path="/" element={<Homepage />} />
                
                {/* Admin Routes */}
                <Route path="/admin/login" element={<Login />} />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Dashboard />} />
                  <Route path="cities" element={<Cities />} />
                  <Route path="lines" element={<Lines />} />
                  <Route path="stations" element={<Stations />} />
                  <Route path="places" element={<Places />} />
                  <Route path="station-connections" element={<StationConnections />} />
                  <Route path="line-stations" element={<LineStations />} />
                  <Route path="station-facilities" element={<StationFacilities />} />
                  <Route path="station-amenities" element={<StationAmenities />} />
                  <Route path="route-planning" element={<RoutePlanning />} />
                  <Route path="analytics" element={<Analytics />} />
                  <Route path="api-logs" element={<ApiLogs />} />
                  <Route path="plans" element={<Plans />} />
                  <Route path="user-plans" element={<UserPlans />} />
                  <Route path="users" element={<Users />} />
                  <Route path="app-versions" element={<AppVersions />} />
                  <Route path="journeys" element={<Journeys />} />
                  <Route path="journey-testing" element={<JourneyTesting />} />
                  <Route path="push-notifications" element={<PushNotifications />} />
                  <Route path="incidents" element={<Incidents />} />
                  <Route path="cache-management" element={<CacheManagement />} />
                  <Route path="fare-rules" element={<FareRules />} />
                  <Route path="fare-conditions" element={<FareConditions />} />
                  <Route path="feature-flags" element={<FeatureFlags />} />
                </Route>
              </Routes>
            </Router>
          </AuthProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App; 